import React, { useEffect, useState } from "react";
import { ArchiveWrapper } from "./styled";
import {
  BackButton,
  Box,
  ButtonStyled,
  HeadingStyled,
  NotAuthorizedForResource,
  SpanStyled,
  ThemeLoader,
} from "../../../../components/styled/Elements";
import { useTranslation } from "react-i18next";
import FloatingInput from "../../../../components/common/inputFields/FloatingInput";
import {
  PlaceholderImg,
  PlusIcon,
  SearchIcon,
} from "../../../../components/styled/AllImages";
import ListTable from "../../../../components/common/tables/ListTable";
import { Col, Row } from "react-bootstrap";
import CustomTile from "../../../../components/common/statusTiles/CustomTile";
import { useNavigate, useParams } from "react-router-dom";
import {
  archiveFilters,
  archiveTileArray,
} from "../../../../helpers/dummyConstants";
import FormRadio from "../../../../components/common/radioButtons/FormRadio";
import { archiveColumns } from "../../../../helpers/adminConstants/TableConstants";
import HistoryModal from "../../../../components/admin/Modals/HistoryModal/HistoryModal";
import Breadcrumbs from "../../../../components/admin/Breadcrumbs/Breadcrumbs";
import {
  getFormattedDate,
  getFormattedTime,
  getModuleOperations,
  hasPlanFeature,
  reqFormatDate,
} from "../../../../utils";
import { DateRangePickersBar } from "../../../../components/common/inputFields/DateRangePickerBar";
import CabinetsApi from "../../../../services/api/cabinetsApi";
import DeleteModal from "../../../../components/admin/Modals/DeleteModal/DeleteModal";
import { toast } from "react-toastify";
import TransferModal from "../components/TransferModal";
import { stepsToTransfer } from "../../../../helpers/adminConstants/AdminConstants";
import RequestModal from "../../../../components/admin/Modals/RequestModal/RequestModal";
import RequestsApi from "../../../../services/api/requestsApi";
import { useUser } from "../../../../context/userContext";

const ASSET_URL = process.env.REACT_APP_ASSET_URL;

const Archives = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = Number(user?.id);
  const userTypeId = Number(user?.user_type_id);
  const { id, fileId } = useParams();
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [archiveId, setArchiveId] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [archivesList, setArchivesList] = useState([]);
  const [historyData, setHistoryData] = useState({});
  const [activePage, setActivePage] = useState(1);
  const [totalItems, setTotalItems] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentOwner, setCurrentOwner] = useState(null);
  const [parentInfo, setParentInfo] = useState(null);
  const [transferSelections, setTransferSelections] = useState(
    Array(stepsToTransfer.length).fill(null)
  );
  const [isTemporary, setIsTemporary] = useState(null);
  const [filterParams, setFilterParams] = useState({
    search_status_type: "",
    search_title: null,
    search_date_from: null,
    search_date_to: null,
  });

  const filteredColumns =
    parseInt(user?.user_type_id) === 6
      ? archiveColumns.filter((col) => col.name !== "privacy")
      : archiveColumns;

  const onDelete = (row) => {
    setArchiveId(row?.hash_id);
    setShowDeleteModal(true);
  };
  const handleEdit = (row) => {
    navigate(
      `/cabinets/${id}/files/${fileId}/archives/edit-archive/${row.hash_id}`
    );
  };
  const handleAdd = async () => {
    try {
      setIsCheckingLimit(true);
      const { message, code } = await CabinetsApi.checkArchiveLimit();
      if (code === 200) {
        navigate(`/cabinets/${id}/files/${fileId}/archives/add-archive`, {
          state: { is_temporary: isTemporary },
        });
      }
      if (code === 429) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
      setIsCheckingLimit(false);
    }
  };

  const handleInfo = (row) => {
    getHistoryData(row.hash_id);
    setShowHistoryModal(true);
  };
  const handleTransfer = (row) => {
    setArchiveId(row.id);
    setShowTransferModal(true);
  };

  const onChange = (e) => {
    let { name, value } = e.target;

    if (name === "search_date_from" || name === "search_date_to") {
      value = reqFormatDate(value);
    }

    setFilterParams((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDelete = async () => {
    try {
      const response = await CabinetsApi.deleteArchive(id, fileId, archiveId);
      if (response.code === 200) {
        toast.success(t("archive_deleted"));
        setShowDeleteModal(false);
        setArchiveId(null);
        getArchivesList();
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  const onViewDetails = (row) => {
    console.log('archive row: ', row);
  setArchiveId(row.id);

  const {
    current_owner,
    current_owner_department_id,
    file_archive_details, // Current Level (Archive)
    file_name,            // Parent Level (File)
    access_granted_requests,
  } = row || {};

  const { department } = user || {};
  const departmentId = Number(department?.id);

  // 1. Basic Ownership Check
  const isLatestOwner = current_owner && userId === Number(current_owner);

  // 2. Permission Evaluation Logic
  // We use .some() to see if ANY detail record grants access
  const isOpenToUser = (file_archive_details || []).some((detail) => {
    let activeDetails = [detail];
    let effectiveSettingId = detail?.common_settings_id;

    /**
     * Fallback Logic: 
     * If common_settings_id is 1 (Inherit), we look at the Parent (File).
     * If File is also 1, we look at the Grandparent (Cabinet).
     */
    if (effectiveSettingId === 1) {
      const fileDetail = file_name?.cabinetfile_details?.[0];
      effectiveSettingId = fileDetail?.common_settings_id;

      if (effectiveSettingId === 1) {
        // Fallback to Cabinet level
        const cabinetDetail = file_name?.cabinet_name?.cabinet_details?.[0];
        effectiveSettingId = cabinetDetail?.setting_id; // Note: Cabinet level uses setting_id
        activeDetails = file_name?.cabinet_name?.cabinet_details || [];
      } else {
        activeDetails = file_name?.cabinetfile_details || [];
      }
    }

    // Now evaluate access against the "activeDetails" array
    return activeDetails.some((item) => {
      const isUserSelected = item?.selected_users?.includes(userId);
      const selectedDepts = item?.selected_departments;
      
      const isDepartmentSelected = Array.isArray(selectedDepts)
        ? selectedDepts.includes(departmentId)
        : Number(selectedDepts) === departmentId;

      // Permission Mapping
      return (
        (effectiveSettingId === 3 && Number(current_owner_department_id) === departmentId) ||
        ([4, 5, 9].includes(effectiveSettingId) && isUserSelected) ||
        ([6, 7].includes(effectiveSettingId) && isDepartmentSelected)
      );
    });
  });

  // 3. Request Access Check
  const hasAccessThroughRequest = Array.isArray(access_granted_requests) &&
    access_granted_requests.some((req) => Number(req?.from_user_id) === userId);

  console.log('userId: ', userId, 'isLatestOwner: ', isLatestOwner, 'isOpenToUser: ', isOpenToUser, 'hasAccessThroughRequest: ', hasAccessThroughRequest, "userTypeId: ", userTypeId);

  // return 
  // 4. Navigation Decision
  if (
    isLatestOwner ||
    isOpenToUser ||
    hasAccessThroughRequest ||
    userTypeId === 6
  ) {
    navigate(`/cabinets/${id}/files/${fileId}/archives/archive-details/${row.hash_id}`);
  } else {
    if (hasPlanFeature(4)) {
      setShowRequestModal(true);
    } else {
      toast.error(t("no_access_to_open_or_request"));
    }
  }
};
  const handleRequest = async () => {
    try {
      setRequestLoading(true);
      const { code, message, error } = await RequestsApi.createRequest({
        module_id: 3,
        module_reference_id: archiveId,
      });
      if (code === 200) {
        toast.success(t("req_sent_successful"));
        setShowRequestModal(false);
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
      if (code === 400 && error) {
        toast.error(error);
      }
    } catch (error) {
    } finally {
      setRequestLoading(false);
    }
  };

  const handleStatusToggle = async (row) => {
    const newObj = {
      archive_id: row.id,
      status: parseInt(row.status) === 1 ? 0 : 1,
    };
    try {
      const response = await CabinetsApi.updateArchiveStatus(
        id,
        fileId,
        newObj
      );
      if (response.code === 200) {
        toast.success(
          `${t("archive")} ${t("status")} ${t("Updated_Successfully")}`
        );
        getArchivesList();
      }
      if (response.code === 204 || response.code === 100) {
        toast.error(response.message);
      }
      if (response?.code === 429) {
        toast.error(response?.error);
      }
    } catch (error) {}
  };

  const getHistoryData = async (archiveId) => {
    try {
      setHistoryLoading(true);
      const { data, code } = await CabinetsApi.getArchiveHistory(
        id,
        fileId,
        archiveId
      );

      if (code === 200) {
        const createdBy = {
          id: data?.created_by_user?.id,
          name: data?.created_by_user?.name,
          subLabel: data?.created_by_user?.user_detail
            ? `${
                language === "en"
                  ? data?.created_by_user?.user_detail?.department_selective
                      ?.name_en
                  : data?.created_by_user?.user_detail?.department_selective
                      ?.name_ar
              } (${
                language === "en"
                  ? data?.created_by_user?.user_detail
                      ?.office_location_selective?.name_en
                  : data?.created_by_user?.user_detail
                      ?.office_location_selective?.name_ar
              })`
            : "",
          avatar: data?.created_by_user?.profile
            ? `${ASSET_URL}/${data?.created_by_user?.profile}`
            : PlaceholderImg,
          date: getFormattedDate(data?.created_at),
          time: getFormattedTime(data?.created_at),
        };

        const editHistory =
          data?.history_update?.length > 0 &&
          data?.history_update?.map((user) => ({
            id: user?.updated_by?.id,
            name: user?.updated_by?.name,
            subLabel: user.updated_by.user_detail
              ? `${
                  language === "en"
                    ? user.updated_by.user_detail.department_selective.name_en
                    : user.updated_by.user_detail.department_selective.name_ar
                } (${
                  language === "en"
                    ? user.updated_by.user_detail.office_location_selective
                        .name_en
                    : user.updated_by.user_detail.office_location_selective
                        .name_ar
                })`
              : "",
            avatar: user?.updated_by?.profile
              ? `${ASSET_URL}/${user?.updated_by?.profile}`
              : PlaceholderImg,
            date: getFormattedDate(user.created_at),
            time: getFormattedTime(user.created_at),
          }));

        const transferHistory =
          data?.transfer_histories.length > 0 &&
          data?.transfer_histories.map((transUser) => ({
            from: {
              id: transUser?.transfer_by?.id,
              name: transUser?.transfer_by?.name,
              subLabel: transUser.transfer_by?.user_detail
                ? `${
                    language === "en"
                      ? transUser.transfer_by?.user_detail?.department_selective
                          ?.name_en
                      : transUser.transfer_by?.user_detail?.department_selective
                          ?.name_ar
                  } (${
                    language === "en"
                      ? transUser?.transfer_by?.user_detail
                          ?.office_location_selective?.name_en
                      : transUser?.transfer_by?.user_detail
                          ?.office_location_selective?.name_ar
                  })`
                : "",
              avatar: transUser?.transfer_by?.profile
                ? `${ASSET_URL}/${transUser?.transfer_by?.profile}`
                : PlaceholderImg,
            },
            to: {
              id: transUser?.transfer_to?.id,
              name: transUser?.transfer_to?.name,
              subLabel: transUser.transfer_to?.user_detail
                ? `${
                    language === "en"
                      ? transUser.transfer_to?.user_detail?.department_selective
                          ?.name_en
                      : transUser.transfer_to?.user_detail?.department_selective
                          ?.name_ar
                  } (${
                    language === "en"
                      ? transUser?.transfer_to?.user_detail
                          ?.office_location_selective?.name_en
                      : transUser?.transfer_to?.user_detail
                          ?.office_location_selective?.name_ar
                  })`
                : "",
              avatar: transUser?.transfer_to?.profile
                ? `${ASSET_URL}/${transUser?.transfer_to?.profile}`
                : PlaceholderImg,
            },
            date: getFormattedDate(transUser.created_at),
            time: getFormattedTime(transUser.created_at),
          }));

        setHistoryData((prevData) => ({
          ...prevData,
          createdBy: createdBy,
          editHistory: editHistory,
          transferHistory: transferHistory,
        }));
      }
    } catch (error) {
    } finally {
      setHistoryLoading(false);
    }
  };

  const getArchivesList = async () => {
    try {
      setAccessDenied(false);
      setLoading(true);
      const { data, code, message } = await CabinetsApi.getArchivesList(
        id,
        fileId,
        activePage,
        filterParams
      );
      if (code === 200) {
        setIsTemporary(data?.parent_is_temporary);

        const archivesArr = data?.archives?.data?.map((archive) => ({
          ...archive,
          from: archive?.from,
          to: archive?.to,
          owner_user: {
            ...archive?.owner_user,
            name:
              parseInt(user?.id) === parseInt(archive?.owner_user?.id)
                ? t("myself")
                : archive?.owner_user?.name,
            profile: archive?.owner_user?.profile
              ? `${ASSET_URL}/${archive?.owner_user?.profile}`
              : PlaceholderImg,
            subLabel:
              archive?.owner_user?.user_detail?.department_selective &&
              archive?.owner_user?.user_detail?.office_location_selective
                ? language === "en"
                  ? `${archive?.owner_user?.user_detail?.department_selective?.name_en} (${archive?.owner_user?.user_detail?.office_location_selective?.name_en})`
                  : `${archive?.owner_user?.user_detail?.department_selective?.name_ar} (${archive?.owner_user?.user_detail?.office_location_selective?.name_ar})`
                : "",
          },
        }));
        setCurrentOwner(data?.file_owner);
        setParentInfo(data?.parent_info);
        setArchivesList(archivesArr);
        setTotalItems(data?.archives?.total);
        setPerPage(data?.archives?.per_page);
        archiveTileArray[0].count = data?.total_archives;
        archiveTileArray[1].count = data?.sent_archives;
        archiveTileArray[2].count = data?.received_archives;
      } else {
        setCurrentOwner(null);
        setArchivesList([]);
        setTotalItems(null);
        setPerPage(null);
        archiveTileArray[0].count = 0;
        archiveTileArray[1].count = 0;
        archiveTileArray[2].count = 0;
      }
      if (code === 403) {
        setAccessDenied(true);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getArchivesList();
  }, [filterParams]);

  const onArchiveTransfer = async () => {
    const selectedRadio = document.querySelector(
      'input[name="trans_user_select"]:checked'
    );
    const selectedId = selectedRadio ? parseInt(selectedRadio.value, 10) : null;
    const updatedData = {
      archive_id: archiveId,
      sent_to: selectedId,
    };
    try {
      setTransferLoading(true);
      const response = await CabinetsApi.transferArchive(
        id,
        fileId,
        updatedData
      );
      if (response.code === 200) {
        toast.success(`${t("archive")} ${t("Transfer")} ${t("Successfully")}`);
        // onHide();
        setShowTransferModal(false);
        setCurrentStep(0);
        setTransferSelections(Array(stepsToTransfer.length).fill(null));
        getArchivesList();
        // navigate("/cabinets")
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <ArchiveWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("archives")}
            onClick={() => navigate(`/cabinets/${id}/files`)}
          />
          <Breadcrumbs />
        </Box>
        <Box className="d-flex gap-2 head-item-two">
          {getModuleOperations(3, 2) &&
            currentOwner &&
            userId === Number(currentOwner?.id) && (
              <ButtonStyled
                label={t("add_archive")}
                icon={PlusIcon}
                onClick={handleAdd}
                disabled={isCheckingLimit}
                isLoading={isCheckingLimit}
              />
            )}
        </Box>
      </Box>
      {accessDenied ? (
        <NotAuthorizedForResource />
      ) : (
        <>
          <Box className="content-container content-container-topBar">
            <Box className="radioFilterContainer">
              {archiveFilters.map((x, i) => (
                <FormRadio
                  name="search_status_type"
                  label={t(x.name)}
                  value={x.id}
                  checked={filterParams.search_status_type == x.id}
                  onChange={onChange}
                  // checked={selected === 2}
                  // onChange={onSelect}
                />
              ))}
            </Box>
            <Box className="rangepickerContainer">
              <Box>
                <SpanStyled size="14px" className="wsNowrap w-r-100">
                  {t("DateRange")}:
                </SpanStyled>
              </Box>
              <DateRangePickersBar
                fromName={"search_date_from"}
                toName={"search_date_to"}
                onChange={onChange}
              />
            </Box>
            <Box className="searchBarContainer p-0">
              <FloatingInput
                height="40px"
                name="search_title"
                type="input"
                fieldType="text"
                label={t("search")}
                placeholder={t("search")}
                bg="var(--white)"
                icon={SearchIcon}
                onChange={onChange}
              />
            </Box>
          </Box>

          <Box className="px-4 mt-4">
            <HeadingStyled color="var(--text)">
              {parentInfo?.name} {t("file")}
            </HeadingStyled>
          </Box>
          <Box className="content-container">
            <Row className="justify-content-center">
              {archiveTileArray.map((tile, key) => (
                <Col lg={3} md={6} sm={12} className="my-2">
                  <CustomTile key={key} {...tile} />
                </Col>
              ))}
            </Row>
          </Box>
          <Box>
            {loading ? (
              <ThemeLoader />
            ) : (
              <ListTable
                options={[
                  "view",
                  "info",
                  "edit",
                  "transfer",
                  "status",
                  "delete",
                ]}
                typeId={3}
                rows={archivesList}
                columns={filteredColumns}
                onViewDetails={onViewDetails}
                onInfoView={handleInfo}
                onEdit={handleEdit}
                perPage={perPage}
                totalCount={totalItems}
                activePage={activePage}
                setActivePage={setActivePage}
                onDelete={onDelete}
                onStatusToggle={handleStatusToggle}
                onTransfer={handleTransfer}
              />
            )}
          </Box>
        </>
      )}
      <HistoryModal
        title={t("ArchiveHistory")}
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        createdBy={historyData.createdBy}
        editHistory={historyData.editHistory}
        transferHistory={historyData.transferHistory}
        historyLoading={historyLoading}
      />

      <TransferModal
        onSubmit={onArchiveTransfer}
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        transferLoading={transferLoading}
        selections={transferSelections}
        setSelections={setTransferSelections}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        // handleTransIds={handleTransIds} // Function to capture selected member IDs
        // cabinetId={cabinetId} // Pass cabinet ID to modal
      />
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title={t("Archive")}
        onDelete={handleDelete}
      />
      <RequestModal
        title={"Archive"}
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        handleRequest={handleRequest}
        isLoading={requestLoading}
      />
    </ArchiveWrapper>
  );
};

export default Archives;
